import type { GridColDef, GridRowClassNameParams } from "@mui/x-data-grid-premium";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { formatMoney, formatNumber, formatPercentage } from "@entrilia/shared/utilities/formatters";
import type { InvestmentLine } from "../../../../../api/types/fundStructureTypes";

const amountColumns: GridColDef<InvestmentLine, number | undefined, string>[] = [
  { field: "commitment", headerName: "Commitment" },
  { field: "unfunded", headerName: "Unfunded" },
  { field: "contributions", headerName: "Contributions" },
  { field: "distributions", headerName: "Distributions" },
  { field: "balance", headerName: "Balance" },
];

const columns: GridColDef<InvestmentLine>[] = [
  { field: "fundName", headerName: "Name", flex: 1.5, minWidth: 100 },
  ...amountColumns.map((column): GridColDef<InvestmentLine, number | undefined, string> => ({
    ...column,
    type: "number",
    flex: 1,
    minWidth: 100,
    valueFormatter: (value: number | undefined, row) => {
      if (value === undefined) {
        return "-";
      }

      return row.currencyCode
        ? formatMoney(value, row.currencyCode, { currencySign: "accounting" })
        : formatNumber(value);
    },
  })),
  {
    field: "netIrr",
    headerName: "Net IRR",
    type: "number",
    flex: 1,
    minWidth: 100,
    valueFormatter: (_value, row) => {
      if (row.isTotalRow) {
        return "";
      }

      return row.netIrr === undefined ? "-" : formatPercentage(row.netIrr, { maximumFractionDigits: 2 });
    },
  },
];

const gridSx = { "& .investment-total-row": { fontWeight: 500 } };

const getRowClassName = ({ row }: GridRowClassNameParams<InvestmentLine>): string =>
  row.isTotalRow ? "investment-total-row" : "";

interface Props {
  investments: InvestmentLine[];
}

const InvestorInvestmentsGrid = ({ investments }: Props) => (
  <DataGrid<InvestmentLine>
    columns={columns}
    rows={investments}
    noRowsText="No data"
    disableColumnPinning
    disableColumnReorder
    disableColumnSorting
    getRowClassName={getRowClassName}
    sx={gridSx}
  />
);

export default InvestorInvestmentsGrid;
